import React, { useRef, useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import type { CareLoadState, StratumKey } from '../lib/types'
import { STRATA_DEFS, getLoadLabel, getLoadColor } from '../lib/types'

interface Props {
  load: CareLoadState
  onChange: (next: CareLoadState) => void
  compact?: boolean
}

const MAX = 10
const VESSEL_H = 320
const VESSEL_W = 168

export default function CareLoadScale({ load, onChange, compact = false }: Props) {
  const svgRef = useRef<SVGSVGElement>(null)
  const [dragging, setDragging] = useState<null | 'total' | StratumKey>(null)

  const total = Math.max(0, Math.min(MAX, load.total))
  const strata = load.strata
  const diffuse = load.diffuse

  const label = getLoadLabel(total)
  const color = getLoadColor(total)

  function updateTotal(nextTotal: number) {
    const clamped = Math.round(nextTotal * 10) / 10
    onChange({ ...load, total: Math.max(0, Math.min(MAX, clamped)) })
  }

  function updateStratum(key: StratumKey, nextVal: number) {
    const clamped = Math.max(0, Math.min(MAX, Math.round(nextVal * 10) / 10))
    const newStrata = strata.map(s => s.key === key ? { ...s, value: clamped } : s)
    // Keep total authoritative but also nudge it toward sum if user is heavily editing strata
    const sum = newStrata.reduce((a, b) => a + b.value, 0)
    const newTotal = load.total > 0 ? load.total : Math.min(MAX, sum)
    onChange({ ...load, strata: newStrata, total: Math.min(MAX, Math.max(load.total, newTotal * 0.6)) })
  }

  // Pointer math for vessel
  const getYFromEvent = useCallback((e: React.PointerEvent | PointerEvent): number => {
    const svg = svgRef.current
    if (!svg) return 0
    const rect = svg.getBoundingClientRect()
    const y = ((e.clientY - rect.top) / rect.height) * VESSEL_H
    return Math.max(0, Math.min(VESSEL_H, y))
  }, [])

  const onPointerDown = (e: React.PointerEvent, which: 'total' | StratumKey) => {
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    setDragging(which)
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging) return
    const y = getYFromEvent(e)
    // Map y (0 at top) to load value (10 at top, 0 at bottom)
    const val = MAX - (y / VESSEL_H) * MAX

    if (dragging === 'total') {
      updateTotal(val)
    } else {
      updateStratum(dragging, val)
    }
  }

  const onPointerUp = () => setDragging(null)

  // Visual stacked height (proportional within the vessel)
  const stratumHeight = (v: number) => (v / MAX) * (VESSEL_H - 24)

  const stratumTops: number[] = []
  let acc = 0
  for (let i = strata.length - 1; i >= 0; i--) {
    stratumTops[i] = acc
    acc += stratumHeight(strata[i].value)
  }

  const totalY = VESSEL_H - (total / MAX) * (VESSEL_H - 24) - 12

  function setDiffuse(d: boolean) {
    onChange({ ...load, diffuse: d })
  }

  return (
    <div className={compact ? "select-none" : "select-none"}>
      {!compact && (
        <div className="flex items-baseline justify-between mb-2">
          <div>
            <div className="section-title">The Care Load Scale</div>
            <div className="text-[11px] text-[#6b7280] mt-0.5">Direct manipulation. The number is yours.</div>
          </div>
          <div className="text-right">
            <div className="text-5xl font-semibold tabular-nums tracking-[-2px] load-number" style={{ color }}>
              {total.toFixed(1)}
            </div>
            <div className="text-[10px] uppercase tracking-[1.5px] text-[#6b7280] -mt-1">out of 10</div>
          </div>
        </div>
      )}

      <div className="relative rounded-3xl bg-[#0a0c14] border border-[#1f2537] p-5">
        <div className="flex gap-5">
          {/* The Vessel */}
          <div>
            <svg
              ref={svgRef}
              width={VESSEL_W}
              height={VESSEL_H}
              viewBox={`0 0 ${VESSEL_W} ${VESSEL_H}`}
              className="scale-vessel block touch-none"
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerLeave={onPointerUp}
            >
              {/* Vessel frame */}
              <rect x="18" y="8" width="132" height={VESSEL_H - 16} rx="8" fill="none" stroke="#2a3146" strokeWidth="8" />
              <rect x="18" y="8" width="132" height={VESSEL_H - 16} rx="8" fill="none" stroke="#1f2537" strokeWidth="2" />

              {/* Subtle interior gradient hint */}
              <rect x="26" y="14" width="116" height={VESSEL_H - 28} rx="3" fill="#11141f" />

              {/* Graduated ticks + labels */}
              {[0,2,4,6,8,10].map((v, i) => {
                const y = VESSEL_H - 12 - (v / MAX) * (VESSEL_H - 24)
                return (
                  <g key={i}>
                    <line x1="12" y1={y} x2="22" y2={y} className="tick" />
                    <text x="6" y={y + 3} fontSize="9" fill="#3a4258" textAnchor="end">{v}</text>
                  </g>
                )
              })}

              {/* Stacked strata (bottom up visually) */}
              {strata.map((s, idx) => {
                const h = stratumHeight(s.value)
                const y = VESSEL_H - 12 - (stratumTops[idx] + h)
                const def = STRATA_DEFS.find(d => d.key === s.key)!
                const stratumColor = s.key === 'cognitive' ? '#4a6359' :
                                     s.key === 'relational' ? '#c17a3f' :
                                     s.key === 'somatic' ? '#8b5e3c' : '#6b7280'
                return (
                  <g key={s.key}>
                    <rect
                      x="28"
                      y={y}
                      width="112"
                      height={Math.max(3, h)}
                      rx="2"
                      fill={stratumColor}
                      opacity={diffuse ? 0.35 : 0.72}
                      onPointerDown={(e) => onPointerDown(e, s.key)}
                      className="cursor-ns-resize"
                    />
                    {/* thin grip line */}
                    {h > 18 && (
                      <line
                        x1="36" y1={y + h / 2} x2="132" y2={y + h / 2}
                        stroke="#fff" strokeOpacity="0.15" strokeWidth="1"
                        onPointerDown={(e) => onPointerDown(e, s.key)}
                        className="cursor-ns-resize"
                      />
                    )}
                  </g>
                )
              })}

              {/* Total meniscus / authoritative marker (gold) */}
              <g
                onPointerDown={(e) => onPointerDown(e, 'total')}
                className="cursor-ns-resize"
              >
                <line
                  x1="22" y1={totalY} x2={VESSEL_W - 22} y2={totalY}
                  stroke={color} strokeWidth="3.5" strokeLinecap="round"
                />
                <circle cx={VESSEL_W / 2} cy={totalY} r="7" fill="#05070f" stroke={color} strokeWidth="2.5" />
                <text x={VESSEL_W / 2} y={totalY + 3} textAnchor="middle" fontSize="9" fontWeight="700" fill={color}>
                  {total.toFixed(1)}
                </text>
              </g>

              {/* Diffuse overlay hint */}
              {diffuse && (
                <rect x="26" y="14" width="116" height={VESSEL_H - 28} rx="3" fill="#c9a227" opacity="0.06" />
              )}
            </svg>
          </div>

          {/* Strata controls + label */}
          <div className="flex-1 min-w-0 pt-1">
            <div className="text-[11px] text-[#6b7280] mb-2 tracking-widest">FELT TOTAL — DRAG THE GOLD LINE OR TYPE</div>

            <div className="flex items-center gap-3 mb-4">
              <input
                type="range"
                min={0}
                max={10}
                step={0.1}
                value={total}
                onChange={e => updateTotal(parseFloat(e.target.value))}
                className="flex-1 accent-[#c9a227]"
              />
              <input
                type="number"
                step="0.1"
                min={0}
                max={10}
                value={total}
                onChange={e => updateTotal(parseFloat(e.target.value) || 0)}
                className="input w-20 text-right font-mono text-lg py-1"
              />
            </div>

            <div className="mb-3">
              <div className="text-xs uppercase tracking-[1px] text-[#6b7280]">Current reading</div>
              <div className="text-xl font-medium tracking-tight" style={{ color }}>{label}</div>
            </div>

            {/* Strata fine controls */}
            <div className="space-y-2.5">
              {strata.map((s) => {
                const def = STRATA_DEFS.find(d => d.key === s.key)!
                return (
                  <div key={s.key} className="flex items-center gap-3 text-sm">
                    <div className="w-24 shrink-0">
                      <div className="font-medium text-[#e2e8f0]">{def.label}</div>
                      <div className="text-[10px] text-[#6b7280] leading-none mt-px">{def.desc}</div>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={10}
                      step={0.1}
                      value={s.value}
                      onChange={e => updateStratum(s.key, parseFloat(e.target.value))}
                      className="flex-1 accent-[#6b7280]"
                    />
                    <div className="w-9 text-right font-mono tabular-nums text-[#e2e8f0] text-sm">{s.value.toFixed(1)}</div>
                  </div>
                )
              })}
            </div>

            <div className="mt-4 flex items-center gap-2">
              <button
                onClick={() => setDiffuse(!diffuse)}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${diffuse ? 'border-[#c9a227] text-[#c9a227] bg-[#1a160a]' : 'border-[#1f2537] text-[#6b7280]'}`}
              >
                {diffuse ? '✓ DIFFUSE — it does not split cleanly' : 'Mark as diffuse / unlocalised'}
              </button>
              {diffuse && (
                <button onClick={() => setDiffuse(false)} className="text-[11px] text-[#6b7280] underline">clear</button>
              )}
            </div>

            <div className="mt-3 text-[10px] text-[#6b7280] leading-snug">
              The gold line is the authoritative total you feel right now. The colored bands are contributors you can adjust. Use "diffuse" when the load resists being split.
            </div>
          </div>
        </div>
      </div>

      {!compact && (
        <div className="mt-2 text-[11px] text-[#6b7280] flex items-center gap-2">
          Drag the gold meniscus or any colored band. Or use the sliders. This number travels with every receipt.
        </div>
      )}
    </div>
  )
}
